import { WebEnv } from '@easy-expense/env-mobile';
import { CheckoutEventNames, Environments, initializePaddle, Paddle } from '@paddle/paddle-js';
import React from 'react';

import { CheckoutSummary, summarizeCheckout } from '../lib/Billing/checkoutSummary';
import {
  PADDLE_FRAME_CLASS,
  PADDLE_FRAME_INITIAL_HEIGHT,
  PADDLE_FRAME_STYLE,
  PADDLE_LOAD_TIMEOUT_MS,
} from '../lib/Billing/paddleInlineFrame';
import { paddleClientTokenProd, paddleClientTokenSandbox } from '../lib/Paddle';

/**
 * `loading` is ours to draw: nothing is mounted yet, so the pane would otherwise be empty.
 * `opening` means Paddle's frame is up and showing its own loading indicator -- the UI must not
 * add a second one on top of it -- but the form has not reported in yet, so it is still not
 * usable and the load backstop still applies.
 */
export type CheckoutFormStatus = 'loading' | 'opening' | 'ready' | 'failed';

export type CheckoutLineItem = { priceId: string; quantity: number };

// Every Paddle event whose payload carries fresh totals for the order summary.
const SUMMARY_EVENTS = new Set<string>([
  'checkout.loaded',
  'checkout.updated',
  'checkout.items.updated',
  'checkout.customer.updated',
  'checkout.discount.applied',
  'checkout.discount.removed',
]);

type CheckoutConfig = {
  /**
   * What is being sold. Null until it can be resolved -- a checkout must never open before the
   * buyer's Trial Eligibility is known, or it sells a trial to someone who may not be owed one.
   * Changing it after the form is up re-prices in place rather than re-opening (which blanks it).
   */
  items: CheckoutLineItem[] | null;
  /** Prefills Paddle's customer field; the buyer can still change it. */
  email: string;
  /** Written onto the Paddle transaction; see PaddleEventCustomDataSchema for what survives. */
  customData: Record<string, string | null>;
  /** Path Paddle redirects to once payment clears, resolved against this origin. */
  successPath: string;
};

function itemsKeyOf(items: CheckoutLineItem[] | null): string | null {
  if (!items) {
    return null;
  }
  return items.map((item) => `${item.priceId}:${item.quantity}`).join(',');
}

/**
 * Drives one Paddle *inline* checkout for a checkout route: initializing the SDK, opening the
 * form once its line items are known, keeping the order summary fed from Paddle's own events,
 * re-pricing in place when the items change, and failing loudly enough to offer a retry.
 *
 * Shared by the Team and Individual checkouts so there is one implementation of the part that is
 * genuinely identical between them -- what differs (who the buyer is, what they may be sold, and
 * how the line item reads) stays in the page.
 */
export function usePaddleCheckout(config: CheckoutConfig): {
  status: CheckoutFormStatus;
  /** Paddle's own figures, or null until the form has reported any. */
  summary: CheckoutSummary | null;
  retry: () => void;
} {
  const [status, setStatus] = React.useState<CheckoutFormStatus>('loading');
  const [summary, setSummary] = React.useState<CheckoutSummary | null>(null);
  const [paddle, setPaddle] = React.useState<Paddle>();
  const [attempt, setAttempt] = React.useState(0);

  const isProduction = WebEnv.buildType === 'production';
  const itemsKey = itemsKeyOf(config.items);

  // The Paddle event callback is registered exactly once per initialization, and `open` is a
  // stable callback the retry path calls directly -- both read the current config through this
  // ref rather than closing over the render that created them.
  const configRef = React.useRef(config);
  configRef.current = config;
  const paddleRef = React.useRef<Paddle | undefined>(undefined);
  const statusRef = React.useRef(status);
  statusRef.current = status;
  /** The items the open form was last told about; null before it has been opened at all. */
  const openedKeyRef = React.useRef<string | null>(null);

  const open = React.useCallback(() => {
    const instance = paddleRef.current;
    const { items, email, customData, successPath } = configRef.current;
    if (!instance || !items) {
      return;
    }
    openedKeyRef.current = itemsKeyOf(items);
    let successUrl = `https://${window.location.host}${successPath}`;
    if (process.env.NODE_ENV === 'development') {
      successUrl = `http://localhost:19006${successPath}`;
    }
    // Paddle draws its own loader inside the frame from here on, so the pane hands the job over
    // rather than stacking ours on top of it.
    setStatus('opening');
    instance.Checkout.open({
      items,
      settings: {
        displayMode: 'inline',
        variant: 'one-page',
        theme: 'light',
        frameTarget: PADDLE_FRAME_CLASS,
        frameStyle: PADDLE_FRAME_STYLE,
        frameInitialHeight: PADDLE_FRAME_INITIAL_HEIGHT,
        successUrl,
        showAddDiscounts: true,
        allowLogout: false,
      },
      customer: { email },
      customData,
    });
  }, []);

  React.useEffect(() => {
    let paddleEnv: Environments = 'production';
    let paddleClientToken = paddleClientTokenProd;
    if (!isProduction) {
      paddleEnv = 'sandbox';
      paddleClientToken = paddleClientTokenSandbox;
    }
    setStatus('loading');
    initializePaddle({
      environment: paddleEnv,
      token: paddleClientToken,
      eventCallback: (event) => {
        // A payment error is recoverable inside the form (bad card, declined) and must not tear
        // it down; a checkout error means the frame itself never came up.
        if (event.name === CheckoutEventNames.CHECKOUT_ERROR) {
          console.error('ERROR with paddle checkout: ', event.code, event.detail);
          setStatus('failed');
          return;
        }
        if (!event.name || !SUMMARY_EVENTS.has(event.name) || !event.data) {
          return;
        }
        setSummary(summarizeCheckout(event.data, new Date()));
        setStatus('ready');
        // The buyer may have changed the quantity while the form was still loading; the frame
        // opened with the original items, so bring it up to date now.
        const wanted = configRef.current.items;
        const loadedQuantity = event.data.items[0]?.quantity;
        if (event.name === 'checkout.loaded' && wanted && loadedQuantity !== wanted[0]?.quantity) {
          openedKeyRef.current = itemsKeyOf(wanted);
          paddleRef.current?.Checkout.updateItems(wanted);
        }
      },
    })
      .then((paddleInstance) => {
        if (paddleInstance) {
          paddleRef.current = paddleInstance;
          setPaddle(paddleInstance);
          return;
        }
        setStatus('failed');
      })
      .catch((error) => {
        console.error('ERROR with paddle: ', error);
        setStatus('failed');
      });
  }, [attempt, isProduction]);

  // Opens the form the moment its items are resolvable, and thereafter re-prices in place.
  // Re-opening on an item change would blank a form the buyer may already be typing into.
  React.useEffect(() => {
    const items = configRef.current.items;
    if (!paddle || !items || !itemsKey) {
      return;
    }
    if (openedKeyRef.current === null) {
      open();
      return;
    }
    if (openedKeyRef.current !== itemsKey && statusRef.current === 'ready') {
      openedKeyRef.current = itemsKey;
      paddle.Checkout.updateItems(items);
    }
  }, [paddle, itemsKey, open]);

  // Backstop for a frame that neither loads nor errors: nothing arrives, so nothing else can
  // move the page off its spinner. Restarted when the items land, so the budget measures how long
  // the form takes rather than being spent waiting for the billing lookup that precedes it.
  React.useEffect(() => {
    // Covers `opening` as well as `loading`: a frame that is blocked outright still gets mounted
    // and still reports nothing, so dropping the backstop once it opens would strand the buyer on
    // Paddle's own spinner with no retry.
    if (status !== 'loading' && status !== 'opening') {
      return;
    }
    const timer = setTimeout(() => setStatus('failed'), PADDLE_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [status, attempt, itemsKey]);

  const retry = React.useCallback(() => {
    setSummary(null);
    if (paddleRef.current) {
      setStatus('loading');
      open();
      return;
    }
    setAttempt((count) => count + 1);
  }, [open]);

  return { status, summary, retry };
}
